/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from 'react';
import type { AssetCategory, ClickResult, HostContext } from '../../@types';
import { getLogger } from '../../utils/SearchLogger';
import { APP_TYPES } from '../SearchResultsPage/SearchConstants';
import AnalyticsUtils from '../../analytics/AnalyticsUtil';
import JumpService from '../../services/JumpService';
import SearchHostContext from '../../utils/SearchHostContext';

export type WrappedComponentProps = {
	onClickUrl?: (context: HostContext, type: string, url: string) => Promise<void>;
};

class ClickBase<T, S = {}> extends Component<T, S> {
	searchHostContext: HostContext = SearchHostContext.getContext();

	/**
	 * Handler for individual results.
	 */
	async onClickCard(searchHostContext: HostContext, type: string, clickResult: ClickResult): Promise<void> {
		if (
			searchHostContext.handledResultCategories.includes(type as AssetCategory) &&
			!clickResult?.item?.attributes?.nonCC
		) {
			// If it's a handledResultCategory, then we pass along the resultItem to the host to handle the click method
			searchHostContext.handleResultClick(clickResult);
			AnalyticsUtils.locationSetByLinks = '';
		} else if (clickResult.action === 'locate') {
			// If this is a locate action and it's not a handledResultCategory, then we open the parent folder
			if (clickResult?.item?.attributes?.parentUrl) {
				const locateURL = clickResult.item.attributes.parentUrl;
				const url = await this.convertToJumpURLIfNeeded(searchHostContext, type, locateURL);
				searchHostContext.openURI(url, false);
			} else {
				getLogger().log('Search Component', 'onClickCard, parentUrl not found for locate action');
			}
		} else if (searchHostContext.openURI && clickResult.item.attributes.openUrl) {
			// Convert the openUrl to a Jump URL, if needed.
			const url = await this.convertToJumpURLIfNeeded(
				searchHostContext,
				type,
				clickResult.item.attributes.openUrl,
			);
			// If it's an unhandledResultCategory, let's just default open up the link using the openURI method.
			getLogger().log('Search Component', 'onClickCard, unhandled AssetCategory type:', type);
			getLogger().log('Search Component', 'onClickCard, default calling hostContext.openURI');
			searchHostContext.openURI(url, clickResult?.item?.attributes?.openInSameTab ?? false);
			AnalyticsUtils.locationSetByLinks = '';
		} else {
			getLogger().error('Search Component', 'searchHostContext.openURI not specified.');
		}
	}

	/**
	 * Handler for open click with JumpURL supported
	 */
	async onClickUrl(
		searchHostContext: HostContext,
		type: string,
		openUrl: string,
		openInSameTab = false,
	): Promise<void> {
		if (this.searchHostContext.openURI) {
			// Convert the openUrl to a Jump URL, if needed.
			const url = await this.convertToJumpURLIfNeeded(this.searchHostContext, type, openUrl);
			this.searchHostContext.openURI(url, openInSameTab);
		} else {
			getLogger().error('Search Component', 'searchHostContext.openURI not specified.');
		}
	}

	/**
	 * Convert this url to an IMS Jump URL, if needed.
	 * @param {string} url - the source url.
	 * @param {string} type - the type of the search category.
	 * @param {boolean} isShowAllUrl - true, if this is a View All/Show All link
	 */
	async convertToJumpURLIfNeeded(
		searchHostContext: HostContext,
		type: string,
		url: string,
		isShowAllUrl = false,
	): Promise<string> {
		let returnUrl = url;

		if (searchHostContext.featureFlags.includes('temp_enable_jump_urls')) {
			// If the host is a desktop surface, fetch the IMS Jump URL for the openUrl
			// to enable single sign-on when the link is opened in a browser.
			if (
				searchHostContext.hostInfo.surface === 'CCDesktop' ||
				searchHostContext.hostInfo.surface === 'CCInApp'
			) {
				try {
					returnUrl = await new JumpService(searchHostContext).getJumpURLforResult(type, url, isShowAllUrl);
				} catch (e) {
					getLogger().error('Search Component', 'Error trying to generate a jump URL');
				}
			}
		}

		return returnUrl;
	}

	/**
	 * Handler for 'Show All' links.
	 */
	async onClickShowAllBase(
		searchHostContext: HostContext,
		type: string,
		resultObject: Record<string, any>,
	): Promise<void> {
		if (searchHostContext.openURI) {
			// Convert the openUrl to a Jump URL, if needed.
			const url = await this.convertToJumpURLIfNeeded(searchHostContext, type, resultObject.openUrl, true);

			switch (type) {
				case 'forums':
				case 'helpcenter':
				case APP_TYPES.STOCK:
				case APP_TYPES.LEARN:
				case APP_TYPES.ADOBE_FORUM:
				case APP_TYPES.LIGHTROOM:
				case APP_TYPES.SPARK:
				case APP_TYPES.SPARK_TEMPLATES:
				case APP_TYPES.APPS:
				case APP_TYPES.PLUGINS:
					searchHostContext.openURI(url, false);
					break;

				case APP_TYPES.CLOUD_DOCS:
				case APP_TYPES.CREATIVE_CLOUD: // creative_cloud
				case APP_TYPES.LIBRARIES:
				case APP_TYPES.MOBILE_CREATIONS:
				case APP_TYPES.XD_ARTIFACTS:
				case APP_TYPES.PRODUCT_INFORMATION:
					searchHostContext.openURI(url, false);
					break;

				default:
					getLogger().info(
						'Search Results Page',
						'Unrecognized result type in onClickShowAllBase handler',
						type,
					);
			}
		} else {
			getLogger().info('Search Component', 'searchHostContext.openURI not specified.');
		}
	}
}

export const withClickBase =
	<T extends WrappedComponentProps>(WrappedComponent: React.ComponentType<T>): React.FC<T & WrappedComponentProps> =>
	(props: T & WrappedComponentProps) => {
		const clickBase = new ClickBase(props);
		return (
			<WrappedComponent
				{...(props as T)}
				onClickUrl={clickBase.onClickUrl.bind(clickBase)}
				onClickCard={clickBase.onClickCard.bind(clickBase)}
			/>
		);
	};

export default ClickBase;
